import React from 'react';
import { createPortal } from 'react-dom';
import { FALLBACK_ACTIVITY_TYPES } from '../../utils/activityIcons';
import type { BoardActivity, BoardEmployee, GridBlock, PlantafelViewMode } from './plantafelTypes';
import { SCHEDULING_STATUS_LABELS } from './plantafelTypes';
import { formatBlockAbsenceDateLabel, formatBlockScheduleLabel } from './plantafelBlockTime';
import { formatHourFraction } from './plantafelBlockTime';

export type TooltipLine = { label: string; value: string };

type ActivityTypeLabel = { id: string; label: string };

/** Instance activity types for tooltip labels; without them custom types would show their id. */
export const PlantafelActivityTypesContext = React.createContext<ActivityTypeLabel[]>(FALLBACK_ACTIVITY_TYPES);

function tooltipLine(label: string, value: string): TooltipLine {
  return { label, value: value.trim() };
}

function assigneeNames(block: GridBlock, employees: BoardEmployee[]): string | null {
  const names = block.assigneeEmployeeIds
    .map((id) => employees.find((e) => e.id === id)?.name?.trim())
    .filter(Boolean) as string[];
  if (names.length === 0) return null;
  return names.join(', ');
}

function userAssigneeNames(activity?: BoardActivity): string | null {
  const names = (activity?.assignee_users ?? [])
    .map((u) => u.display_name?.trim() || u.email?.trim() || '')
    .filter(Boolean);
  if (names.length === 0) return null;
  return names.join(', ');
}

export function buildBlockTooltip(
  block: GridBlock,
  employees: BoardEmployee[],
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  activity?: BoardActivity,
  timeZone?: string,
  activityTypes: ActivityTypeLabel[] = FALLBACK_ACTIVITY_TYPES,
): { heading: string; lines: TooltipLine[] } {
  const lines: TooltipLine[] = [];

  if (block.isAbsence) {
    lines.push(tooltipLine('Zeitraum', formatBlockAbsenceDateLabel(viewportStart, block, timeZone)));
    if (block.absenceTimedHours != null) {
      lines.push(
        tooltipLine(
          'Uhrzeit',
          `${formatHourFraction(block.hourStart)} – ${formatHourFraction(block.hourEnd)} Uhr`,
        ),
      );
      lines.push(
        tooltipLine('Dauer', `${block.absenceTimedHours.toFixed(1).replace('.', ',')} Stunden`),
      );
    }
    const name = assigneeNames(block, employees);
    if (name) lines.push(tooltipLine('Mitarbeiter', name));
    return { heading: block.title, lines };
  }

  if (block.orderNo) lines.push(tooltipLine('Vorgang', block.orderNo));
  if (block.customer !== '—') lines.push(tooltipLine('Kontakt', block.customer));
  const leadTitle = activity?.lead_title?.trim();
  if (leadTitle && leadTitle !== block.title) lines.push(tooltipLine('Projekt', leadTitle));
  const typeLabel =
    activityTypes.find((t) => t.id === block.activityType)?.label ??
    FALLBACK_ACTIVITY_TYPES.find((t) => t.id === block.activityType)?.label ??
    block.activityType;
  lines.push(tooltipLine('Typ', typeLabel));
  lines.push(tooltipLine('Status', SCHEDULING_STATUS_LABELS[block.status]));
  if (block.priority === 'dringend') lines.push(tooltipLine('Priorität', 'Dringend'));
  lines.push(tooltipLine('Termin', formatBlockScheduleLabel(viewportStart, block, timeZone)));

  const crew = assigneeNames(block, employees);
  if (crew) lines.push(tooltipLine('Einsatzteam', crew));

  const projectLeads = userAssigneeNames(activity);
  if (projectLeads) lines.push(tooltipLine('Projektleiter', projectLeads));

  const note = activity?.notes?.trim();
  if (note) {
    lines.push(
      tooltipLine('Beschreibung', note.length > 160 ? `${note.slice(0, 157)}…` : note),
    );
  }

  return { heading: block.title, lines };
}

export function PlantafelBlockTooltipPortal({
  content,
  anchorRect,
}: {
  content: { heading: string; lines: TooltipLine[] };
  anchorRect: DOMRect;
}) {
  const maxW = 300;
  const gap = 10;
  const edge = 8;
  const ref = React.useRef<HTMLDivElement>(null);
  // Die Höhe ist erst nach dem Rendern bekannt; bis dahin bleibt die Blase unsichtbar, damit sie
  // nicht kurz an der falschen Stelle aufblitzt. Unten kein Platz → Blase über dem Block.
  const [pos, setPos] = React.useState<{ top: number; placement: 'below' | 'above' } | null>(null);

  let left = anchorRect.left;
  if (left + maxW > window.innerWidth - 12) {
    left = Math.max(12, window.innerWidth - maxW - 12);
  }

  React.useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 0;
    const below = anchorRect.bottom + gap;
    if (below + h <= window.innerHeight - edge) {
      setPos({ top: below, placement: 'below' });
      return;
    }
    const above = anchorRect.top - gap - h;
    if (above >= edge) {
      setPos({ top: above, placement: 'above' });
      return;
    }
    // Passt weder oben noch unten ganz: dort anzeigen, wo mehr Platz ist, und an den Rand klemmen.
    const roomBelow = window.innerHeight - anchorRect.bottom;
    const roomAbove = anchorRect.top;
    setPos(
      roomBelow >= roomAbove
        ? { top: Math.max(edge, window.innerHeight - edge - h), placement: 'below' }
        : { top: edge, placement: 'above' },
    );
  }, [anchorRect, content]);

  // Pfeil zeigt auf die Mitte des Blocks, bleibt aber innerhalb der Blase.
  const arrowX = Math.min(
    maxW - 18,
    Math.max(18, anchorRect.left + anchorRect.width / 2 - left),
  );
  const placement = pos?.placement ?? 'below';

  return createPortal(
    <div
      ref={ref}
      className="pointer-events-none fixed z-[250] max-w-[300px] rounded-lg border border-app-border bg-app-surface px-3 py-2.5 shadow-lg"
      style={{ top: pos?.top ?? 0, left, width: maxW, visibility: pos ? 'visible' : 'hidden' }}
      role="tooltip"
    >
      <span
        aria-hidden
        className={`absolute h-2.5 w-2.5 rotate-45 border-app-border bg-app-surface ${
          placement === 'below' ? '-top-[6px] border-l border-t' : '-bottom-[6px] border-b border-r'
        }`}
        style={{ left: arrowX - 5 }}
      />
      <div className="text-sm font-semibold leading-snug text-app-text">{content.heading}</div>
      <ul className="mt-1.5 space-y-1">
        {content.lines.map((line) => (
          <li key={`${line.label}-${line.value}`} className="text-xs leading-snug">
            <span className="font-medium text-app-text-subtle">{line.label}: </span>
            <span className="text-app-text-muted">{line.value}</span>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  );
}

export function usePlantafelBlockTooltip(
  block: GridBlock,
  employees: BoardEmployee[],
  viewportStart: Date,
  viewMode: PlantafelViewMode,
  activity?: BoardActivity,
  timeZone?: string,
) {
  const [hover, setHover] = React.useState(false);
  const [rect, setRect] = React.useState<DOMRect | null>(null);
  const ref = React.useRef<HTMLDivElement>(null);

  const show = () => {
    const el = ref.current;
    if (!el) return;
    setRect(el.getBoundingClientRect());
    setHover(true);
  };

  const hide = () => {
    setHover(false);
    setRect(null);
  };

  const activityTypes = React.useContext(PlantafelActivityTypesContext);
  const content = React.useMemo(
    () => buildBlockTooltip(block, employees, viewportStart, viewMode, activity, timeZone, activityTypes),
    [block, employees, viewportStart, viewMode, activity, timeZone, activityTypes],
  );

  return {
    ref,
    show,
    hide,
    tooltip:
      hover && rect ? <PlantafelBlockTooltipPortal content={content} anchorRect={rect} /> : null,
  };
}
